/**
 * useAdjustments Hook
 * Manages forecast adjustments (flat amounts or percentages) layered on top of driver values
 */

import { useState, useCallback, useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  getAdjustments,
  createAdjustment,
  updateAdjustment,
  deleteAdjustment,
  type ForecastAdjustment,
  type CreateAdjustmentParams,
  type UpdateAdjustmentParams,
  type GetAdjustmentsResponse,
  type AdjustmentType,
} from '../api';
import { forecastQueryKeys } from './useForecastData';
// eslint-disable-next-line import/no-relative-packages
import { formatCurrencyRounded } from '../../../utils/currency';

// ============ Query Keys ============

export const adjustmentQueryKeys = {
  all: ['forecast-v3', 'adjustments'] as const,
  byAccount: (versionId: number, chartOfAccountId: number, locationId?: number) =>
    [...adjustmentQueryKeys.all, { versionId, chartOfAccountId, locationId }] as const,
};

// ============ Hook Parameters ============

interface UseAdjustmentsParams {
  versionId: number;
  chartOfAccountId: number;
  locationId?: number;
  includeExpired?: boolean;
  enabled?: boolean;
}

// ============ Return Types ============

interface UseAdjustmentsReturn {
  // Data
  adjustments: ForecastAdjustment[];
  activeAdjustments: ForecastAdjustment[];
  expiredAdjustments: ForecastAdjustment[];
  activeCount: number;

  // Loading states
  isLoading: boolean;
  isCreating: boolean;
  isUpdating: boolean;
  isDeleting: boolean;

  // Error state
  error: Error | null;

  // Actions
  createAdjustment: (params: Omit<CreateAdjustmentParams, 'versionId' | 'chartOfAccountId' | 'locationId'>) => Promise<ForecastAdjustment>;
  updateAdjustment: (id: number, params: Omit<UpdateAdjustmentParams, 'id' | 'versionId' | 'chartOfAccountId' | 'locationId'>) => Promise<ForecastAdjustment>;
  deleteAdjustment: (id: number) => Promise<void>;
  refetch: () => void;

  // Helpers
  getAdjustmentsForMonth: (year: number, month: number) => ForecastAdjustment[];
  isExpired: (adjustment: ForecastAdjustment) => boolean;
  isExpiringSoon: (adjustment: ForecastAdjustment, daysThreshold?: number) => boolean;
}

// ============ Helper Functions ============

/**
 * Check if an adjustment has expired
 */
function checkIsExpired(adjustment: ForecastAdjustment): boolean {
  if (!adjustment.expiresAt) return false;
  return new Date(adjustment.expiresAt) < new Date();
}

/**
 * Check if an adjustment is expiring soon (within specified days)
 */
function checkIsExpiringSoon(adjustment: ForecastAdjustment, daysThreshold: number = 7): boolean {
  if (!adjustment.expiresAt) return false;
  const expiresAt = new Date(adjustment.expiresAt);
  const now = new Date();
  const threshold = new Date(now.getTime() + daysThreshold * 24 * 60 * 60 * 1000);
  return expiresAt > now && expiresAt <= threshold;
}

/**
 * Extract YYYYMM numeric value from an ISO date string.
 * Parses the string directly to avoid timezone-related off-by-one month issues
 * that occur when a UTC midnight date (e.g., "2026-02-01T00:00:00.000Z") shifts
 * to the previous day in local time (e.g., Jan 31 in US timezones).
 */
function toYYYYMM(dateStr: string): number {
  // ISO strings are "YYYY-MM-DDT..." - parse year and month directly from the string
  const iso = typeof dateStr === 'string' ? dateStr : new Date(dateStr).toISOString();
  const year = parseInt(iso.substring(0, 4), 10);
  const month = parseInt(iso.substring(5, 7), 10);
  return year * 100 + month;
}

/**
 * Check if an adjustment applies to a specific month
 * Uses YYYYMM numeric comparison with timezone-safe date parsing
 */
function adjustmentAppliesToMonth(adjustment: ForecastAdjustment, year: number, month: number): boolean {
  // Parse dates from ISO strings directly to avoid timezone offset issues
  const monthValue = year * 100 + month;
  const startValue = toYYYYMM(adjustment.startMonth);
  const endValue = toYYYYMM(adjustment.endMonth);

  const isInRange = monthValue >= startValue && monthValue <= endValue;
  const isNotExpired = !checkIsExpired(adjustment);

  return isInRange && isNotExpired;
}

// ============ Main Hook ============

export function useAdjustments({
  versionId,
  chartOfAccountId,
  locationId,
  includeExpired = true,
  enabled = true,
}: UseAdjustmentsParams): UseAdjustmentsReturn {
  const queryClient = useQueryClient();

  // Query key for this specific account's adjustments
  const queryKey = adjustmentQueryKeys.byAccount(versionId, chartOfAccountId, locationId);

  // Fetch adjustments
  const {
    data,
    isLoading,
    error,
    refetch,
  } = useQuery<GetAdjustmentsResponse, Error>({
    queryKey,
    queryFn: () => getAdjustments({
      versionId,
      chartOfAccountId,
      locationId,
      includeExpired,
    }),
    enabled: enabled && versionId > 0 && chartOfAccountId > 0,
  });

  // Helper to invalidate all related queries after adjustment changes
  const invalidateAllQueries = () => {
    // Invalidate adjustment queries
    queryClient.invalidateQueries({ queryKey: adjustmentQueryKeys.all });
    // Invalidate forecast calculation queries to trigger grid recalculation
    queryClient.invalidateQueries({ queryKey: forecastQueryKeys.all });
  };

  // Create mutation
  const createMutation = useMutation({
    mutationFn: (params: Omit<CreateAdjustmentParams, 'versionId' | 'chartOfAccountId' | 'locationId'>) =>
      createAdjustment({
        ...params,
        versionId,
        chartOfAccountId,
        locationId: locationId || 0,
      }),
    onSuccess: invalidateAllQueries,
  });

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: ({ id, ...params }: { id: number } & Omit<UpdateAdjustmentParams, 'id' | 'versionId' | 'chartOfAccountId' | 'locationId'>) =>
      updateAdjustment({
        id,
        ...params,
        versionId,
        chartOfAccountId,
        locationId: locationId || 0,
      }),
    onSuccess: invalidateAllQueries,
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: deleteAdjustment,
    onSuccess: invalidateAllQueries,
  });

  // Memoized adjustment lists
  const adjustments = useMemo(() => data?.adjustments || [], [data]);

  const activeAdjustments = useMemo(
    () => adjustments.filter(a => !checkIsExpired(a)),
    [adjustments]
  );

  const expiredAdjustments = useMemo(
    () => adjustments.filter(a => checkIsExpired(a)),
    [adjustments]
  );

  // Create action
  const handleCreate = useCallback(
    async (params: Omit<CreateAdjustmentParams, 'versionId' | 'chartOfAccountId' | 'locationId'>) => {
      return createMutation.mutateAsync(params);
    },
    [createMutation]
  );

  // Update action
  const handleUpdate = useCallback(
    async (id: number, params: Omit<UpdateAdjustmentParams, 'id' | 'versionId' | 'chartOfAccountId' | 'locationId'>) => {
      return updateMutation.mutateAsync({ id, ...params });
    },
    [updateMutation]
  );

  // Delete action
  const handleDelete = useCallback(
    async (id: number) => {
      await deleteMutation.mutateAsync(id);
    },
    [deleteMutation]
  );

  // Get adjustments for a specific month
  const getAdjustmentsForMonth = useCallback(
    (year: number, month: number) => {
      return activeAdjustments.filter(a => adjustmentAppliesToMonth(a, year, month));
    },
    [activeAdjustments]
  );

  return {
    // Data
    adjustments,
    activeAdjustments,
    expiredAdjustments,
    activeCount: data?.activeCount || 0,

    // Loading states
    isLoading,
    isCreating: createMutation.isPending,
    isUpdating: updateMutation.isPending,
    isDeleting: deleteMutation.isPending,

    // Error state
    error: error || null,

    // Actions
    createAdjustment: handleCreate,
    updateAdjustment: handleUpdate,
    deleteAdjustment: handleDelete,
    refetch,

    // Helpers
    getAdjustmentsForMonth,
    isExpired: checkIsExpired,
    isExpiringSoon: checkIsExpiringSoon,
  };
}

// ============ Utility Types for Components ============

export interface AdjustmentFormValues {
  adjustmentType: AdjustmentType;
  value: number;
  description: string;
  startMonth: Date;
  endMonth: Date;
  expiresAt: Date | null;
}

/**
 * Format an adjustment value for display
 */
export function formatAdjustmentValue(
  adjustmentType: AdjustmentType,
  value: number,
  currency?: string | null,
): string {
  if (adjustmentType === 'FLAT') {
    return formatCurrencyRounded(value, currency);
  }
  return `${value}%`;
}

/**
 * Format a month date range for display
 * Parses dates as UTC to avoid timezone offset issues
 */
export function formatMonthRange(startMonth: string, endMonth: string): string {
  // Parse ISO strings directly to avoid timezone offset causing off-by-one month
  const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

  const startYYYYMM = toYYYYMM(startMonth);
  const endYYYYMM = toYYYYMM(endMonth);

  const startStr = `${monthNames[(startYYYYMM % 100) - 1]} ${Math.floor(startYYYYMM / 100)}`;
  const endStr = `${monthNames[(endYYYYMM % 100) - 1]} ${Math.floor(endYYYYMM / 100)}`;

  if (startStr === endStr) {
    return startStr;
  }
  return `${startStr} - ${endStr}`;
}

export default useAdjustments;
